<script lang="ts" setup>
import { CreateProjectRequest, Project } from '@beeboard/common';
import {
  helpers,
  minValue,
  numeric,
  required,
  url,
} from '@vuelidate/validators';
import Holidays from 'date-holidays';
import { DateTime } from 'luxon';
import ProjectImageUpload from '@/app/project/ProjectImageUpload.vue';
import UserTile from '@/app/template/UserTile.vue';
import { useFeatureFlagStore } from '@/store/featureFlagStore';
import { useProfileStore } from '@/store/profileStore';
import { useProjectStore } from '@/store/projectStore';
import { useTemplateStore } from '@/store/templateStore';
import { useUserRulesStore } from '@/store/userRulesStore';
import { useUserStore } from '@/store/userStore';
import { MultiSelectOption } from '@/ui/form/UiMultiSelect.vue';
import { diff } from '@/utils/helpers/diff';
import {
  hasMirroredEnabled,
  trimSlashes,
} from '@/utils/helpers/urlManipulation';
import { requiresHttps, requiresUrlPath } from '@/utils/validation/formRules';

type ImageMode = 'url' | 'upload';

const props = withDefaults(
  defineProps<{
    form: CreateProjectRequest;
    loading: boolean;
    projectId?: string | null;
    isEdit?: boolean;
    maxDate?: string | null;
    hasCustomProperties?: boolean;
    manageResponsible?: boolean;
    // The image file picked earlier in the creation wizard, shown again.
    image?: File | null;
  }>(),
  {
    isEdit: false,
    maxDate: null,
    projectId: null,
    hasCustomProperties: true,
    manageResponsible: false,
    image: null,
  },
);

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'back'): void;
  (e: 'update:image', image: File | null): void;
  (
    e: 'next',
    form: CreateProjectRequest,
    projectImage: File | null,
    deleteImage: boolean,
  ): void;
}>();

const userStore = useUserStore();
const profileStore = useProfileStore();
const templateStore = useTemplateStore();
const projectStore = useProjectStore();
const featureFlagStore = useFeatureFlagStore();

const formData = ref(props.form);
const isLoading = ref(true);
const imageMode = ref<ImageMode>(props.image ? 'upload' : 'url');
const projectImage = ref<File | null>(props.image);
const deleteImage = ref(false);
const canCreateProjectsWithoutScope =
  useUserRulesStore().canCreateProjectsWithoutScope();

// "Ende der ersten Phase" of a blank project: without it the create check
// fails, so it blocks the button like the other required fields (WI #5780).
const firstDeadlineRequired = helpers.withMessage(
  'Value is required',
  (deadlines: CreateProjectRequest['deadlines']) => !!deadlines?.[0],
);

const rules = {
  title: [required],
  responsible: [required],
  start_date: [required],
  deadlines: props.form.template ? [] : [firstDeadlineRequired],
  scopes: canCreateProjectsWithoutScope ? [] : [required],
  volume: [numeric, minValue(1)],
  project_image_url: [url],
  project_documents_path: featureFlagStore.hasDocumentPaths
    ? [requiresUrlPath, requiresHttps, url]
    : [],
};

const editRules = {
  title: [required],
  responsible: [required],
  volume: [numeric, minValue(1)],
  project_image_url: [url],
  project_documents_path: featureFlagStore.hasDocumentPaths
    ? [requiresUrlPath, requiresHttps, url]
    : [],
};

const possibleScopes = ref<MultiSelectOption[]>([]);
const userScopes = profileStore.profile.scopes;

const users = computed(() => {
  return userStore.users.map((user) => ({
    label: user.username as string,
    value: user.id,
  }));
});

const projectMembers = computed(() =>
  props.projectId && projectStore.hasMembers(props.projectId)
    ? projectStore.projectMembers(props.projectId).map((user) => ({
        label: user.username as string,
        value: user.id,
      }))
    : [],
);

const templateScopes = computed(
  () =>
    (props.form.template &&
      templateStore.templateLink(props.form.template)?.scopes) ||
    [],
);

const scopes = computed({
  get: () => {
    return formData.value.scopes || [];
  },

  set: (value) => {
    formData.value.scopes = value;
  },
});

const eligibleParentProjects = computed(() => {
  return projectStore.projectSummaries
    .filter(
      (project) =>
        project.is_active &&
        project.id !== props.projectId &&
        Boolean(project.parent_id) === false,
    )
    .map((project) => ({
      label: project.title,
      value: project.id,
    }));
});

const endOfFirstPhaseMinDate = computed(() => {
  if (!formData.value.start_date) {
    return '';
  }

  return DateTime.fromISO(formData.value.start_date)
    .plus({ days: 10 })
    .toISODate();
});

const actionButtonText = computed(() => {
  // In edit mode, show "Update"
  if (props.isEdit) {
    return $t('button.update');
  }

  // In create mode, show "Next" if custom properties exist, otherwise "Create"
  if (props.hasCustomProperties || props.form.template) {
    return $t('button.next');
  }

  return $t('button.create');
});

const cancelButtonText = computed(() =>
  props.isEdit ? $t('button.cancel') : $t('button.back'),
);

function extractData(
  data: Project | CreateProjectRequest,
  responsible: string,
) {
  return {
    title: data.title,
    responsible: responsible,
    volume: Number(data.volume) || null,
    number: data.number,
    address: data.address,
    description: data.description,
    scopes: data.scopes,
    parent_id: data.parent_id,
    start_date: DateTime.fromISO(data.start_date),
    project_image_url: data.project_image_url,
    project_documents_path: featureFlagStore.hasDocumentPaths
      ? data.project_documents_path
      : undefined,
    calendar: data.calendar || [],
  };
}

const isPristine = computed(() => {
  // Guard: Don't compute if still loading
  if (props.loading) {
    return true;
  }

  const project =
    props.projectId &&
    projectStore.hasProject(props.projectId) &&
    projectStore.project(props.projectId);
  if (!project) {
    return false;
  }
  const projectData = extractData(project, project.responsible?.id);
  const form = extractData(formData.value, formData.value.responsible);
  if (
    selectedCountry.value !== (projectData.calendar?.[0] ?? '') ||
    selectedState.value !== (projectData.calendar?.[1] ?? '') ||
    selectedRegion.value !== (projectData.calendar?.[2] ?? '')
  ) {
    return false;
  }

  return (
    Object.keys(diff(projectData, form)).length === 0 &&
    !projectImage.value &&
    !deleteImage.value
  );
});

const scopesReady = ref(false);

onMounted(async () => {
  // manageResponsible scopes the user list by the project, which isn't loaded
  // until the parent finishes (after this child mounts), so defer to the watch.
  if (!props.manageResponsible) {
    await userStore.loadUsers({
      projectReader: getScopes(),
    });
  }

  await userStore.loadScopes();
  isLoading.value = false;
  scopesReady.value = true;
  if (!props.loading) {
    await setScopesOptions();
  }
});

// Recompute once the parent has loaded the project. The scopesReady guard keeps
// this from firing setScopesOptions a second time alongside onMounted.
watch(
  () => props.loading,
  async (loading) => {
    if (!loading && scopesReady.value) {
      await setScopesOptions();
    }
  },
);

if (!props.form.template && !props.isEdit) {
  watch(endOfFirstPhaseMinDate, (value) => {
    if (value && value > formData.value.deadlines[0]) {
      formData.value.deadlines[0] = '';
    }
  });
}

// Counts setScopesOptions calls, so a slower earlier call cannot overwrite the
// result of a later one.
let scopesOptionsCall = 0;

async function setScopesOptions() {
  const call = ++scopesOptionsCall;
  if (props.manageResponsible) {
    await userStore.loadUsers({ projectReader: getProjectScopes() });
  }

  let scopes = props.isEdit
    ? userScopes.project_reader
    : userScopes.project_creator;

  if (formData.value.responsible) {
    const user = await userStore.loadUserProfile(formData.value.responsible);
    const userScopes = user.scopes.project_reader || [];
    scopes = scopes.filter((s) => userScopes.includes(s));
  }
  if (call !== scopesOptionsCall) {
    return;
  }
  if (
    props.form.template &&
    templateStore.hasTemplateSummary(props.form.template)
  ) {
    scopes = scopes.filter((s) => templateScopes.value.includes(s));
  }
  if (!props.isEdit) {
    keepAllowedScopes(scopes);
  }

  if (props.projectId && projectStore.hasProject(props.projectId)) {
    const projectScopes = projectStore.project(props.projectId).scopes;
    scopes = Array.from(new Set([...scopes, ...projectScopes]));
  }

  possibleScopes.value = scopes.map((s) => {
    const scopeDetails = userStore.scope(s);

    return {
      label: scopeDetails.name,
      value: scopeDetails.id,
    };
  });
}

// When creating, the selection keeps only scopes the project may get: coming
// back to the step keeps it as the user left it, a new responsible removes the
// scopes they cannot read, and it never grows by itself (WI #5780). Assigned
// only on a change, so an untouched field does not show its required error.
function keepAllowedScopes(allowedScopes: string[]) {
  const selected = formData.value.scopes ?? [];
  const kept = selected.filter((scope) => allowedScopes.includes(scope));
  if (kept.length !== selected.length) {
    formData.value.scopes = kept;
  }
}

function getUser(userId: string | number | undefined) {
  const user = userStore.users.find((a) => a.id === userId);

  return {
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
  };
}

const parentProjectAvailable = hasMirroredEnabled();

function getProjectScopes(): string[] {
  return props.projectId && projectStore.hasProject(props.projectId)
    ? (projectStore.project(props.projectId).scopes ?? [])
    : [];
}

function getScopes() {
  if (props.manageResponsible) {
    return getProjectScopes();
  }
  if (canCreateProjectsWithoutScope) {
    return [];
  }
  if (props.isEdit) {
    return userScopes.project_reader;
  }
  const creatorScopes = userScopes.project_creator;
  if (props.form.template) {
    return creatorScopes.filter((s) => templateScopes.value.includes(s));
  }

  return creatorScopes;
}

function calendarCodes() {
  return [
    selectedCountry.value,
    selectedState.value,
    selectedRegion.value,
  ].filter((code) => !!code);
}

// Back to the previous wizard step: the form edits the wizard's data in place,
// only the calendar picks are local, so they go along (WI #5780).
function goBack() {
  formData.value.calendar = calendarCodes();
  emit('back');
}

function setProjectImage(image: File | null) {
  projectImage.value = image;
  emit('update:image', image);
}

function submitForm() {
  if (!formData.value.responsible) {
    return;
  }

  const payload: CreateProjectRequest = {
    template: formData.value.template,
    title: formData.value.title,
    start_date: formData.value.start_date,
    volume: Number(formData.value.volume) || null,
    number: formData.value.number,
    description: formData.value.description,
    address: formData.value.address,
    responsible: formData.value.responsible,
    scopes: formData.value.scopes,
    parent_id: formData.value.parent_id,
    deadlines: formData.value.deadlines,
    project_image_url: formData.value.project_image_url,
    calendar: calendarCodes(),
  };

  // Only include project_documents_path if the feature is enabled
  if (
    featureFlagStore.hasDocumentPaths &&
    formData.value.project_documents_path
  ) {
    payload.project_documents_path = trimSlashes(
      formData.value.project_documents_path,
    );
  }

  emit('next', payload, projectImage.value, deleteImage.value);
}

const hd = new Holidays();

const mainCountries = [
  'Deutschland',
  'France',
  'Nederland',
  'Österreich',
  'Schweiz',
  'Sverige',
  'United Kingdom',
];

const countryOptions = computed(() => {
  const countries = hd.getCountries();
  const options = Object.entries(countries).map(([code, country]) => ({
    label: country,
    value: code,
  }));

  options.sort((a, b) => {
    const aIsMain = mainCountries.includes(a.label);
    const bIsMain = mainCountries.includes(b.label);

    if (aIsMain && bIsMain) {
      // Sort by the order specified in mainCountries
      return mainCountries.indexOf(a.label) - mainCountries.indexOf(b.label);
    }
    if (aIsMain) {
      return -1;
    }
    if (bIsMain) {
      return 1;
    }

    // Both not in mainCountries, sort alphabetically
    return a.label.localeCompare(b.label);
  });

  return options;
});
const selectedCountry = ref(formData.value.calendar?.[0] || '');
const selectedState = ref(formData.value.calendar?.[1] || '');
const selectedRegion = ref(formData.value.calendar?.[2] || '');

// Watch for changes to the calendar array and update the selection
watch(
  () => formData.value.calendar,
  (newCalendar) => {
    if (newCalendar && newCalendar.length) {
      selectedCountry.value = newCalendar[0] || '';
      selectedState.value = newCalendar[1] || '';
      selectedRegion.value = newCalendar[2] || '';
    }
  },
  { immediate: true },
);

const stateOptions = computed(() => {
  const states = hd.getStates(selectedCountry.value);
  if (!states) {
    return [];
  }

  return Object.entries(states).map(([code, stateName]) => ({
    label: stateName,
    value: code,
  }));
});

const regionOptions = computed(() => {
  const regions = hd.getRegions(selectedCountry.value, selectedState.value);
  if (!regions) {
    return [];
  }

  return Object.entries(regions).map(([code, regionName]) => ({
    label: regionName,
    value: code,
  }));
});

watch(selectedCountry, (newCountry) => {
  hd.init(newCountry);
});
watch(selectedState, (newState) => {
  hd.init(selectedCountry.value, newState || undefined);
  selectedRegion.value = '';
});
watch(selectedRegion, (newRegion) => {
  hd.init(selectedCountry.value, selectedState.value, newRegion || undefined);
});

// Re-sync imageMode once form data loads (EditProject populates it async after mount)
watch(
  [
    () => props.form.project_image_url,
    () =>
      props.projectId ? projectStore.imageUrls[props.projectId] : undefined,
  ],
  ([url, blobUrl]) => {
    if (!props.isEdit) {
      return;
    }
    if (blobUrl) {
      imageMode.value = 'upload';
    } else if (url) {
      imageMode.value = 'url';
    }
  },
  { immediate: true },
);

function setCountry() {
  selectedState.value = '';
  selectedRegion.value = '';
}

function setState() {
  selectedRegion.value = '';
}

function selectImageMode(mode: ImageMode) {
  if (imageMode.value === mode) {
    return;
  }
  imageMode.value = mode;
  if (mode === 'upload') {
    formData.value.project_image_url = '';
  }
}
</script>

<template>
  <div>
    <p v-if="!props.isEdit" class="subtitle">
      {{ $t('project.fillInTheDataSubtitle') }}
    </p>

    <UiForm
      :form-data="formData"
      :rules="props.isEdit ? editRules : rules"
      @form-submit="submitForm"
    >
      <template #default="{ formErrors }">
        <div class="duo-input u-padding-bottom">
          <UiInput
            id="Input-title"
            v-model="formData.title"
            :label="`${$t('project.title')}*`"
            :errors="formErrors.title"
          />
          <UiSelect
            id="project-responsible"
            v-model="formData.responsible"
            :options="
              props.projectId && !props.manageResponsible
                ? projectMembers
                : users
            "
            name="responsible"
            :label="`${$t('project.projectResponsible')}*`"
            :errors="formErrors.responsible"
            @update:model-value="setScopesOptions"
          >
            <template #selection="{ selection }">
              <UserTile
                :first-name="getUser(selection?.value)?.firstName || ''"
                :last-name="getUser(selection?.value)?.lastName || ''"
                :email="getUser(selection?.value)?.email || ''"
              />
            </template>
            <template #option="{ option }">
              <UserTile
                :first-name="getUser(option?.value)?.firstName || ''"
                :last-name="getUser(option?.value)?.lastName || ''"
                :email="getUser(option?.value)?.email || ''"
              />
            </template>
          </UiSelect>
        </div>

        <div class="duo-input u-padding-bottom">
          <div class="duo-input">
            <UiField
              :label="`${$t('project.startDate')}*`"
              :errors="formErrors.start_date"
            >
              <UIDatePicker
                id="start-date"
                v-model="formData.start_date"
                :max-date="props.maxDate ?? undefined"
              />
            </UiField>

            <UiField
              v-if="!props.isEdit && !props.form.template"
              :label="`${$t('project.endOfFirstPhase')} *`"
              :errors="formErrors.deadlines"
            >
              <UIDatePicker
                id="end-date"
                v-model="(formData.deadlines as string[])[0]"
                :disabled="!formData.start_date"
                :min-date="endOfFirstPhaseMinDate ?? undefined"
              />
            </UiField>
          </div>

          <div class="full-width">
            <UiInput
              id="project-volume"
              v-model="formData.volume"
              :label="`${$t('project.volume')}`"
              :errors="formErrors.volume"
            />
          </div>
        </div>

        <div class="duo-input u-padding-bottom">
          <UiInput
            id="project-number"
            v-model="formData.number"
            :label="`${$t('project.projectNumber')}`"
          />
          <UiInput
            id="project-address"
            v-model="formData.address"
            :label="`${$t('project.address')}`"
          />
        </div>

        <div class="duo-input u-padding-bottom">
          <label class="ui-label">{{ $t('calendar.title') }}</label>
          <UiSelect
            v-model="selectedCountry"
            name="country-picker"
            :label="`${$t('calendar.country')}`"
            :options="countryOptions"
            @update:model-value="setCountry"
          />
          <UiSelect
            v-model="selectedState"
            :disabled="stateOptions.length === 0"
            name="state-picker"
            :label="`${$t('calendar.state')}`"
            :options="stateOptions"
            @update:model-value="setState"
          />
          <UiSelect
            v-model="selectedRegion"
            :disabled="regionOptions.length === 0"
            name="region-picker"
            :label="`${$t('calendar.region')}`"
            :options="regionOptions"
          />
        </div>

        <slot name="after-calendar" />

        <div class="duo-input u-padding-bottom">
          <div class="full-width">
            <UiMultiSelectField
              id="project-scopes"
              v-model="scopes"
              :disabled="!formData.responsible"
              :options="possibleScopes"
              :label="`${$t('project.scope')}`"
              :errors="formErrors.scopes"
              @update:model-value="
                (newValue: string[]) => (formData.scopes = newValue)
              "
            />
            <span v-if="!formData.responsible">
              {{ $t('project.selectResponsibleFirst') }}
            </span>
          </div>

          <div v-if="parentProjectAvailable" class="full-width">
            <UiSelect
              id="project-parent"
              v-model="formData.parent_id"
              name="projectParent"
              :options="eligibleParentProjects"
              :label="`${$t('project.parentProject')}`"
            />
          </div>
        </div>

        <div class="image-section u-padding-bottom">
          <div class="image-options">
            <div
              class="image-option"
              :class="{ 'image-option--inactive': imageMode !== 'url' }"
            >
              <label class="image-option-header">
                <RadioButton
                  input-id="imageMode-url"
                  name="imageMode"
                  value="url"
                  :model-value="imageMode"
                  @update:model-value="selectImageMode('url')"
                />
                <UiIcon name="Link2" class="image-option-icon" />
                <span class="image-option-label">
                  {{ $t('project.imageUrl') }}
                </span>
              </label>
              <UiInput
                id="project-image-url"
                v-model="formData.project_image_url"
                placeholder="https://example.com/image.png"
                :errors="formErrors.project_image_url"
                @focus="selectImageMode('url')"
              />
            </div>
            <span class="image-option-or">{{ $t('common.or') }}</span>
            <div
              class="image-option"
              :class="{ 'image-option--inactive': imageMode !== 'upload' }"
            >
              <label class="image-option-header">
                <RadioButton
                  input-id="imageMode-upload"
                  name="imageMode"
                  value="upload"
                  :model-value="imageMode"
                  @update:model-value="selectImageMode('upload')"
                />
                <UiIcon name="ArrowUpToLine" class="image-option-icon" />
                <span class="image-option-label">{{
                  $t('project.image.upload')
                }}</span>
              </label>
              <ProjectImageUpload
                :project-id="props.projectId"
                :initial-file="projectImage"
                @image-file="setProjectImage"
                @delete-image="deleteImage = $event"
                @click="selectImageMode('upload')"
              />
            </div>
          </div>
        </div>

        <div class="duo-input duo-input--top">
          <div
            v-if="featureFlagStore.hasDocumentPaths"
            class="duo-input__item u-padding-bottom"
          >
            <UiInput
              id="project-documents-path"
              v-model="formData.project_documents_path"
              :label="`${$t('project.documentsPath')}`"
              placeholder="https://tenant.sharepoint.com/sites/project/Documents"
              :errors="formErrors.project_documents_path"
              :disabled="props.isEdit"
            />
          </div>

          <!-- Filled only when editing: project mail needs a project to belong to. -->
          <slot name="after-documents-path" />
        </div>

        <AppWysiwygEditor
          id="project-description"
          v-model="formData.description"
          tool-bar-position="bottom"
          :label="$t('message.description')"
        />
      </template>

      <template #bottom="{ hasErrors, formSubmit }">
        <div class="actions">
          <UiButtonRow
            size="medium"
            :cancel-button-text="cancelButtonText"
            :disabled="hasErrors || isPristine"
            :loading="props.loading"
            :action-button-text="actionButtonText"
            has-space-between
            @cancel="props.isEdit ? emit('cancel') : goBack()"
            @action="formSubmit"
          />
        </div>
      </template>
    </UiForm>
  </div>
</template>

<style lang="scss" scoped>
.subtitle {
  color: var(--color-gray-500);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-14);
  line-height: var(--line-height-20);
}

.duo-input {
  display: flex;
  gap: var(--space-16);
  width: 100%;
  align-items: center;

  &--top {
    align-items: flex-start;
  }

  &__item {
    flex: 1;
    min-width: 0;
  }
}

.full-width {
  width: 100%;
}

.u-padding-bottom {
  padding-bottom: var(--space-4);
}

:deep(.ui-field) {
  width: 100%;
}

.image-section {
  width: 100%;
}

.image-options {
  display: flex;
  align-items: flex-start;
  gap: var(--space-12);
  width: 100%;
}

.image-option {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  transition: opacity var(--duration-fast);

  &--inactive {
    opacity: 0.4;
  }
}

.image-option-header {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-14);
  color: var(--color-gray-800);
  cursor: pointer;
  user-select: none;
}

.image-option-icon {
  flex-shrink: 0;
  width: px(16);
  height: px(16);
}

.image-option-label {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-14);
}

.image-option-or {
  align-self: center;
  color: var(--color-gray-400);
  font-size: var(--font-size-13);
  flex-shrink: 0;
  padding-top: var(--space-24);
}

.ui-label {
  flex-grow: 1;
  height: 20px;
  font-style: normal;
  line-height: var(--line-height-20);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-700);
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
}
</style>
